import React from 'react';
import moment from 'moment-timezone';

// Utilities
import BravelyData from 'utils/data';
import { mixpanelEvents, mixpanelTrack } from 'utils/mixpanelHelpers';
import { getPageContent } from 'webinars/pages/WebinarConfirmationPage/helpers/contentHelpers';

// Components
import RegistrationBanner from 'shared/components/LandingPageCTA/RegistrationBanner';
import LandingPageDropdownButton from 'shared/components/LandingPageCTA/LandingPageDropdownButton';
import RegistrationButton from 'shared/components/LandingPageCTA/RegistrationButton';
import Timer, { getDateTimeRemaining } from 'webinars/components/Timer';

// x(minutes) * 60 * 1000 = y(miliseconds)
export const JOIN_WINDOW = 10 * 60 * 1000;

const preEventBannerText = {
  primaryCtaBanner: 'Register to save your spot!',
  secondaryCtaBanner:
    'Register to reserve your spot in this live webinar and receive the recording afterwards!',
  aboutToStartBanner: 'This webinar is starting soon!',
  isHappeningNowBanner: 'This webinar is in progress!',
};

const ctaLocation = {
  primaryCtaBanner: 'Top',
  secondaryCtaBanner: 'Bottom',
};

const {
  webinars: { CLICK_WEBINAR_CTA },
} = mixpanelEvents;

export const getBannerText = ({
  bannerType,
  isPast,
  isRegistered,
  recordingUrl,
  isAboutToStart,
  isLoggedIn,
  isHappeningNow,
}) => {
  if (isAboutToStart && (!isLoggedIn || !isRegistered)) {
    return preEventBannerText['aboutToStartBanner'];
  } else if (isHappeningNow) {
    return preEventBannerText['isHappeningNowBanner'];
  } else if (isPast) {
    return `The recording of this webinar  ${
      Boolean(recordingUrl) ? 'is now available.' : 'will be available soon.'
    }`;
  } else {
    return isRegistered
      ? "You're registered for this webinar!"
      : preEventBannerText[bannerType];
  }
};

export const headers = {
  header: 'About this webinar',
  subHeader: "In this webinar, you'll:",
};

export const getButtonProps = ({
  isPast,
  isRegistered,
  webinarID,
  fromMailOrCalendar,
  recordingUrl,
  countDownTimer,
  isAboutToStart,
  isHappeningNow,
  name,
  formattedDate,
}) => {
  // Have to encode the header params individually
  // as they may contain special characters (, & etc)
  const encodedName = encodeURIComponent(name);
  const encodedDate = encodeURIComponent(formattedDate);

  const queryParams = `?header_title=${encodedName}&header_date=${encodedDate}&webinar_id=${webinarID}`;
  const disabled = Boolean(countDownTimer);

  if (isPast && !isHappeningNow) {
    return {
      text: 'Watch Now',
      link: `/webinars/${webinarID}/watch${queryParams}`,
      disabled: !recordingUrl,
    };
  } else if (
    isRegistered ||
    fromMailOrCalendar ||
    isAboutToStart ||
    isHappeningNow
  ) {
    return {
      text: 'Join Now',
      link: `${location.pathname}/join${queryParams}`,
      disabled,
      target: `${BravelyData('useZoomSdk') ? '' : '_blank'}`,
    };
  } else {
    return {
      text: 'Register Now',
      link: `${location.pathname}/confirmation${queryParams}`,
      disabled,
    };
  }
};

const isWithin45MinAfterStart = (scheduledDate) => {
  // add the window of 45 minutes to scheduled date time
  const past45Minutes = moment(scheduledDate);
  past45Minutes.set({ minute: moment(scheduledDate).minutes() + 45 });

  const now = new Date();
  return (
    !scheduledDate ||
    (moment(now).isAfter(moment(scheduledDate)) &&
      moment(now).isBefore(past45Minutes))
  );
};

export const renderRegistrationBanner = ({
  bannerType,
  isPast,
  isRegistered,
  isLoggedIn,
  showTimer,
  setShowTimer,
  scheduledDate,
  fromMailOrCalendar,
  encodedId,
  recordingUrl,
  name,
  formattedDate,
  googleLink,
  outlookLink,
}) => {
  const isHappeningNow = isWithin45MinAfterStart(scheduledDate);
  const isAboutToStart =
    !isPast && getDateTimeRemaining(scheduledDate) < JOIN_WINDOW;

  let bannerText = getBannerText({
    bannerType,
    isPast,
    isRegistered,
    isAboutToStart,
    isLoggedIn,
    recordingUrl,
    isHappeningNow,
  });
  const scheduledTask = (isAboutToStart, isLoggedIn) => {
    if (isAboutToStart && (!isLoggedIn || !isRegistered))
      bannerText = getBannerText(preEventBannerText['aboutToStartBanner']);
  };

  const countDownTimer = !isPast && isRegistered && showTimer && (
    <Timer
      scheduledDate={scheduledDate}
      showTimer={showTimer}
      setShowTimer={setShowTimer}
      scheduledTask={scheduledTask.bind(null, isAboutToStart, isLoggedIn)}
      disableAt={JOIN_WINDOW}
    />
  );

  const buttonProps = {
    ...getButtonProps({
      isPast,
      isRegistered,
      isLoggedIn,
      isAboutToStart,
      webinarID: encodedId,
      fromMailOrCalendar,
      recordingUrl,
      countDownTimer,
      isHappeningNow,
      name,
      formattedDate,
    }),
    onClick: () => {
      mixpanelTrack(CLICK_WEBINAR_CTA, {
        encodedId,
        name,
        cta: buttonProps.text,
        ctaLocation: ctaLocation[bannerType],
      });
    },
  };

  let buttons = [
    <RegistrationButton key={'registration-btn'} {...buttonProps} />,
  ];

  if (
    !isAboutToStart &&
    !isHappeningNow &&
    isRegistered &&
    bannerType === 'primaryCtaBanner'
  ) {
    const { buttonProps: calendarButtonProps } = getPageContent({
      name,
      encodedId,
      googleLink,
      outlookLink,
    });

    buttons = [
      <LandingPageDropdownButton
        key={'calender-btn'}
        {...calendarButtonProps}
      />,
      ...buttons,
    ];
  }

  return (
    <RegistrationBanner
      text={bannerText}
      buttons={buttons}
      countDownTimer={countDownTimer}
    />
  );
};
